/**
 * Structured Data Component for SEO
 *
 * Renders JSON-LD schemas in the page for Google rich results.
 * Schemas are based on template defaults, NOT personalized values.
 *
 * This enables:
 * - HowTo rich snippets in search results
 * - FAQ accordions in search results
 * - Breadcrumb trails in search results
 */

import { Component  } from 'react'
import {
  buildCanonicalUrl,
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateHowToSchema,
} from '../utils'
import type {ReactNode} from 'react';
import type { ProjectTemplate } from '../types'

interface StructuredDataErrorBoundaryProps {
  children: ReactNode
}

interface StructuredDataErrorBoundaryState {
  hasError: boolean
}

class StructuredDataErrorBoundary extends Component<
  StructuredDataErrorBoundaryProps,
  StructuredDataErrorBoundaryState
> {
  constructor(props: StructuredDataErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): StructuredDataErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('StructuredData rendering error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return null
    }
    return this.props.children
  }
}

interface ProjectGuideStructuredDataProps {
  template: ProjectTemplate
  canonicalUrl?: string
}

function StructuredDataScripts({
  template,
  canonicalUrl,
}: ProjectGuideStructuredDataProps) {
  const url = canonicalUrl ?? buildCanonicalUrl(template)

  const howToSchema = generateHowToSchema(template, url)
  const faqSchema = template.faqItems?.length
    ? generateFAQSchema(template.faqItems)
    : null
  const breadcrumbSchema = generateBreadcrumbSchema(template, url)

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  )
}

export function ProjectGuideStructuredData(
  props: ProjectGuideStructuredDataProps
) {
  return (
    <StructuredDataErrorBoundary>
      <StructuredDataScripts {...props} />
    </StructuredDataErrorBoundary>
  )
}
